import { useEffect } from 'react';

const RELOADED_KEY = 'bo_reloaded_for';

/**
 * Guard de la autoactualización: recargar COMO MUCHO UNA VEZ por versión
 * detectada. Si tras recargar el bundle sigue siendo viejo (p. ej. la API se
 * redesplegó pero este contenedor no se reconstruyó), sin esto se entra en un
 * bucle de recargas infinito.
 */
export function shouldReloadForVersion(
  current: string | undefined,
  deployed: string | undefined,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = sessionStorage
): boolean {
  if (!current || !deployed) return false;
  if (deployed === current) {
    storage.removeItem(RELOADED_KEY); // al día: rearma para el próximo deploy
    return false;
  }
  if (storage.getItem(RELOADED_KEY) === deployed) return false; // ya recargado
  storage.setItem(RELOADED_KEY, deployed);
  return true;
}

// Autoactualización: compara la versión compilada con la que sirve /api/version
// al montar y al volver a primer plano; si difieren, recarga para coger el
// build nuevo (clave si el backoffice se añade a la pantalla de inicio).
export function useAutoUpdate() {
  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        const res = await fetch('/api/version', { cache: 'no-store' });
        if (!res.ok) return;
        const { version } = await res.json();
        if (!stop && shouldReloadForVersion(import.meta.env.VITE_APP_VERSION, version)) {
          stop = true;
          window.location.reload();
        }
      } catch {
        /* sin conexión: reintenta al próximo foco */
      }
    };
    check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stop = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
